// useEditorMutations - Facade hook for all FountainEditor content editing mutations
// Routes saveText() to the correct mutation by entityType × field, manages save state lifecycle,
// and provides tag/character/location attachment operations.

import { useCallback, useMemo, useRef } from 'react';
import { useAttachEntityTag } from '@/api/mutations/useAttachEntityTag';
import { useDeleteCharacter, useUpdateCharacter } from '@/api/mutations/useCharacterMutations';
import { useCreateCharacterAndAttach } from '@/api/mutations/useCreateCharacterAndAttach';
import { useCreateLineAndAttach } from '@/api/mutations/useCreateLineAndAttach';
import { useCreateLocationAndAttach } from '@/api/mutations/useCreateLocationAndAttach';
import { useCreateFeature, useDeleteFeature, useUpdateFeature } from '@/api/mutations/useFeatureMutations';
import { useUpdateActLogline } from '@/api/mutations/useUpdateActLogline';
import { useUpdateActSynopsis } from '@/api/mutations/useUpdateActSynopsis';
import { useUpdateFragmentLogline } from '@/api/mutations/useUpdateFragmentLogline';
import { useUpdateFragmentSynopsis } from '@/api/mutations/useUpdateFragmentSynopsis';
import { useUpdateSceneLogline } from '@/api/mutations/useUpdateSceneLogline';
import { useUpdateSceneSynopsis } from '@/api/mutations/useUpdateSceneSynopsis';
import type { NavigatorSaveStateSetter } from '@/contexts/NavigatorSaveStateContext';
import { type EntityType, getTextAtomId } from '@/data/activeTextAtom';
import { featureTypeId, type SignCharacter } from '@/enums';
import { useProjectId } from '@/hooks';
import { useReadOnlyGate } from '@/hooks/useReadOnlyGate';
import type { IFountainAct, IFountainFragment, IFountainScene } from '@/types';

// Re-exported, not re-declared — the union belongs to `data/activeTextAtom.ts`.
export type { EntityType };

type TextField = 'logline' | 'plot';

interface SaveTextOptions {
    onEditorSync?: (sceneId: number, newLogline: string) => void;
    /** Which atom to write, when the caller knows and the ACTIVE one is not it
     *  (#1841 item 11): an act can have several loglines and the sheet draws
     *  them all, so a draft row's field saves to its own atom rather than to
     *  the entity's active one. Left out, the active atom answers — and when
     *  there is none, the write creates one, which is how a field with nothing
     *  active takes its first words from the plan. */
    textAtomId?: number;
}

export interface FountainMutations {
    saveText(
        entityType: EntityType,
        entity: IFountainAct | IFountainFragment | IFountainScene,
        field: TextField,
        content: string,
        options?: SaveTextOptions,
    ): void;
    attachTags(entityType: EntityType, entityId: number, addTags: number[], removeTags?: number[]): void;
    detachTags(entityType: EntityType, entityId: number, tagIds: number[]): void;
    createLineAndAttach(entityType: EntityType, entityId: number, title: string, color: string, removeTags?: number[]): void;
    createCharacterAndAttach(entityType: EntityType, entityId: number, name: string, ageCategory?: string): void;
    createLocationAndAttach(
        entityType: EntityType,
        entityId: number,
        name: string,
        description?: string,
        sublocationName?: string,
        removeTags?: number[],
    ): void;
    editCharacter(characterId: number, data: { name: string; age_category?: string; category?: SignCharacter }): void;
    deleteCharacter(characterId: number): void;
    editLocation(locationId: number, data: { name: string; description?: string; sublocationNames?: string[] }): void;
    deleteLocation(locationId: number): void;
    isReadOnly: boolean;
}

export function useEditorMutations(saveStateContext?: NavigatorSaveStateSetter | null): FountainMutations {
    const projectId = useProjectId();
    const { isReadOnly } = useReadOnlyGate();

    // Compose all mutation hooks. Destructure only the stable .mutate / .mutateAsync
    // — the wrapping result objects from useMutation are recreated on every render,
    // which would otherwise cascade through useCallback deps and force every consumer
    // of NavigatorActions to re-render on each render of this hook.
    const { mutate: updateActLogline } = useUpdateActLogline();
    const { mutate: updateActPlot } = useUpdateActSynopsis();
    const { mutate: updateFragmentLogline } = useUpdateFragmentLogline();
    const { mutate: updateFragmentPlot } = useUpdateFragmentSynopsis();
    const { mutate: updateSceneLogline } = useUpdateSceneLogline();
    const { mutate: updateScenePlot } = useUpdateSceneSynopsis();
    const { mutate: attachEntityTag } = useAttachEntityTag();
    const { mutate: createLineAndAttachMutate } = useCreateLineAndAttach();
    const { mutate: createCharacterAndAttachMutate } = useCreateCharacterAndAttach();
    const { mutate: createLocationAndAttachMutate } = useCreateLocationAndAttach();
    const { mutate: deleteCharacterMutate } = useDeleteCharacter();
    const { mutate: deleteFeatureMutate } = useDeleteFeature();
    const { mutateAsync: createFeatureAsync } = useCreateFeature();
    const { mutateAsync: updateCharacterAsync } = useUpdateCharacter();
    const { mutateAsync: updateFeatureAsync } = useUpdateFeature();

    // Helper: wrap mutation with save state lifecycle.
    // saveStateContext is read via a ref so that callers downstream (e.g. NavigatorActions)
    // keep stable identities across saveStateContext value changes.
    const saveStateContextRef = useRef(saveStateContext);
    saveStateContextRef.current = saveStateContext;
    const withSaveState = useCallback(
        (saveKey: string, mutateFn: (callbacks: { onSuccess: () => void; onError: (error: Error) => void }) => void) => {
            saveStateContextRef.current?.setSaveState(saveKey, { status: 'saving' });
            mutateFn({
                onSuccess: () => {
                    saveStateContextRef.current?.setSaveState(saveKey, {
                        status: 'saved',
                        lastSaved: new Date(),
                    });
                },
                onError: (error: Error) => {
                    saveStateContextRef.current?.setSaveState(saveKey, {
                        status: 'error',
                        error: error.message,
                    });
                },
            });
        },
        [],
    );

    const saveText = useCallback(
        (
            entityType: EntityType,
            entity: IFountainAct | IFountainFragment | IFountainScene,
            field: TextField,
            content: string,
            options?: SaveTextOptions,
        ) => {
            const textAtomId = options?.textAtomId ?? getTextAtomId(entityType, entity, field);
            const saveKey = `${entityType === 'fragment' ? 'episode' : entityType}-${entity.id}-${field === 'plot' ? 'plot' : 'logline'}`;

            withSaveState(saveKey, ({ onSuccess, onError }) => {
                if (entityType === 'act') {
                    const mutate = field === 'logline' ? updateActLogline : updateActPlot;
                    mutate({ actId: entity.id, content, textAtomId }, { onSuccess, onError });
                } else if (entityType === 'fragment') {
                    const mutate = field === 'logline' ? updateFragmentLogline : updateFragmentPlot;
                    mutate({ fragmentId: entity.id, content, textAtomId }, { onSuccess, onError });
                } else if (field === 'logline') {
                    updateSceneLogline(
                        {
                            sceneId: entity.id,
                            content,
                            textAtomId,
                            onEditorSync: options?.onEditorSync,
                        },
                        { onSuccess, onError },
                    );
                } else {
                    updateScenePlot(
                        {
                            sceneId: entity.id,
                            content,
                            textAtomId,
                            onEditorSync: options?.onEditorSync,
                        },
                        { onSuccess, onError },
                    );
                }
            });
        },
        [withSaveState, updateActLogline, updateActPlot, updateFragmentLogline, updateFragmentPlot, updateSceneLogline, updateScenePlot],
    );

    const attachTags = useCallback(
        (entityType: EntityType, entityId: number, addTags: number[], removeTags?: number[]) => {
            attachEntityTag({
                entityType,
                entityId,
                addTags,
                removeTags,
            });
        },
        [attachEntityTag],
    );

    const detachTags = useCallback(
        (entityType: EntityType, entityId: number, tagIds: number[]) => {
            attachEntityTag({
                entityType,
                entityId,
                removeTags: tagIds,
            });
        },
        [attachEntityTag],
    );

    const createLineAndAttach = useCallback(
        (entityType: EntityType, entityId: number, title: string, color: string, removeTags?: number[]) => {
            createLineAndAttachMutate({
                title,
                color,
                entityType,
                entityId,
                removeTagIds: removeTags,
            });
        },
        [createLineAndAttachMutate],
    );

    const createCharacterAndAttach = useCallback(
        (entityType: EntityType, entityId: number, name: string, ageCategory?: string) => {
            createCharacterAndAttachMutate({
                name,
                age_category: ageCategory,
                entityType,
                entityId,
            });
        },
        [createCharacterAndAttachMutate],
    );

    const createLocationAndAttach = useCallback(
        (entityType: EntityType, entityId: number, name: string, description?: string, sublocationName?: string, removeTags?: number[]) => {
            createLocationAndAttachMutate({
                name,
                description,
                sublocationName,
                entityType,
                entityId,
                removeTags,
            });
        },
        [createLocationAndAttachMutate],
    );

    const editCharacter = useCallback(
        async (characterId: number, data: { name: string; age_category?: string; category?: SignCharacter }) => {
            if (!projectId) return;
            await updateCharacterAsync({
                project_id: projectId,
                character_id: characterId,
                data,
            });
        },
        [projectId, updateCharacterAsync],
    );

    const deleteCharacter = useCallback(
        (characterId: number) => {
            if (!projectId) return;
            deleteCharacterMutate({ project_id: projectId, personage_id: characterId });
        },
        [projectId, deleteCharacterMutate],
    );

    const editLocation = useCallback(
        async (locationId: number, data: { name: string; description?: string; sublocationNames?: string[] }) => {
            if (!projectId) return;
            const formData = new FormData();
            formData.append('name', data.name);
            if (data.description) formData.append('description', data.description);
            await updateFeatureAsync({
                project_id: projectId,
                feature_id: locationId,
                data: formData,
            });
            if (data.sublocationNames?.length) {
                for (const subName of data.sublocationNames) {
                    await createFeatureAsync({
                        project_id: projectId,
                        data: {
                            name: subName,
                            feature_type_id: featureTypeId.location,
                            parent_id: locationId,
                        },
                    });
                }
            }
        },
        [projectId, updateFeatureAsync, createFeatureAsync],
    );

    const deleteLocation = useCallback(
        (locationId: number) => {
            if (!projectId) return;
            deleteFeatureMutate({ project_id: projectId, feature_id: locationId });
        },
        [projectId, deleteFeatureMutate],
    );

    return useMemo(
        () => ({
            saveText,
            attachTags,
            detachTags,
            createLineAndAttach,
            createCharacterAndAttach,
            createLocationAndAttach,
            editCharacter,
            deleteCharacter,
            editLocation,
            deleteLocation,
            isReadOnly,
        }),
        [
            saveText,
            attachTags,
            detachTags,
            createLineAndAttach,
            createCharacterAndAttach,
            createLocationAndAttach,
            editCharacter,
            deleteCharacter,
            editLocation,
            deleteLocation,
            isReadOnly,
        ],
    );
}
